// src/utils/haulerMapPrivacy.js
//
// Viewer-aware privacy transform for the Hauler map and card surfaces.
//
// The booking row stores accurate per-address coordinates (`requests.lat`
// / `requests.lng`). Available/unclaimed jobs visible to an unrelated
// Hauler must NOT publish that rooftop point. But the same Hauler app
// is also seen by:
//   - the booking owner (`requests.user_id === viewer`) when they have
//     a dual customer + Hauler account,
//   - the assigned Hauler (`requests.assigned_driver === viewer`) — this
//     case is normally handled by the separate `claimedJobs` render
//     path, but the predicate is here for completeness/safety,
//   - admins / elevated users.
//
// Those viewers may see the exact coordinate (and the exact address on
// the card) because they have a legitimate need to. Unrelated viewers
// always see the zone centroid + the "Address shown after claim"
// privacy copy.
//
// This is the single source of truth for "can this viewer see the
// exact location of this job?". Any Hauler-side surface should call
// `canViewerSeeExactLocation(job, viewerUserId, opts)` rather than
// rolling its own ownership predicate.

// Hard service-area boundary for Hauler map display coordinates.
// Coordinates are [lng, lat]. Map dots/clusters must never render outside
// this polygon; if a fallback or stored coordinate drifts, we fail closed
// to an inside service-area point instead of publishing a marker elsewhere.
export const HAULER_SERVICE_AREA_POLYGON = [
  [-112.0130418, 41.1171536],
  [-112.0126386, 41.1176836],
  [-112.011957, 41.1178458],
  [-112.0108684, 41.118056],
  [-112.0101955, 41.1176154],
  [-112.0095184, 41.117673],
  [-112.0086873, 41.1174634],
  [-112.0080511, 41.1170087],
  [-112.006616, 41.1165697],
  [-112.0061526, 41.1162736],
  [-112.005125, 41.1158691],
  [-112.0040593, 41.1152293],
  [-112.0026915, 41.1145051],
  [-112.0024622, 41.113846],
  [-112.002566, 41.1129238],
  [-112.001923, 41.1125396],
  [-112.0017291, 41.1122364],
  [-112.0016306, 41.1112971],
  [-112.0003136, 41.1107655],
  [-111.9993216, 41.1109027],
  [-111.9980637, 41.1107105],
  [-111.9973799, 41.1105974],
  [-111.9969439, 41.110189],
  [-111.9963101, 41.1099081],
  [-111.9957078, 41.1093861],
  [-111.9951357, 41.1088959],
  [-111.9949136, 41.1084921],
  [-111.995274, 41.1080266],
  [-111.9946423, 41.1082686],
  [-111.9943554, 41.1080077],
  [-111.9940567, 41.1080684],
  [-111.9937016, 41.1078507],
  [-111.9932739, 41.1076672],
  [-111.9923612, 41.1069959],
  [-111.9914713, 41.1066129],
  [-111.9906129, 41.1061014],
  [-111.9906509, 41.1051093],
  [-111.9905309, 41.1047959],
  [-111.990417, 41.1044685],
  [-111.9905232, 41.1041146],
  [-111.9910261, 41.1038299],
  [-111.9918039, 41.1038558],
  [-111.9933697, 41.1038603],
  [-111.9966396, 41.1038655],
  [-111.9991631, 41.104029],
  [-112.00167, 41.1044062],
  [-112.003724, 41.1057967],
  [-112.0038898, 41.1064413],
  [-112.0047643, 41.1070902],
  [-112.0048462, 41.107563],
  [-112.0047554, 41.108155],
  [-112.004799, 41.1085174],
  [-112.0054307, 41.1085223],
  [-112.0067031, 41.1085661],
  [-112.0067149, 41.1100294],
  [-112.0067075, 41.1109454],
  [-112.0083931, 41.1111041],
  [-112.0091368, 41.1115099],
  [-112.0096136, 41.1118647],
  [-112.0097049, 41.1122608],
  [-112.0098142, 41.1127652],
  [-112.0094949, 41.1132597],
  [-112.0093418, 41.1137642],
  [-112.0097046, 41.1142593],
  [-112.0103714, 41.114923],
  [-112.0104655, 41.1152921],
  [-112.0105608, 41.1156157],
  [-112.011124, 41.1159947],
  [-112.0116339, 41.1163971],
  [-112.0123207, 41.1165322],
  [-112.0130418, 41.1171536],
];

const ZONE_CENTROIDS = {
  A: [41.1155, -112.0088],
  B: [41.1122, -112.0065],
  C: [41.1086, -112.0015],
  D: [41.1078, -111.9960],
};
const DEFAULT_FALLBACK = [41.1090, -112.0020];

export function isInHaulerServiceArea(lat, lng) {
  const y = Number(lat);
  const x = Number(lng);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;

  let inside = false;
  const polygon = HAULER_SERVICE_AREA_POLYGON;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersects = ((yi > y) !== (yj > y)) &&
      (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Decide whether the current viewer is authorized to see the exact
 * rooftop location of a job. Pure function; safe to call in render.
 *
 * Allowed when ANY of:
 *  - `opts.isAdmin === true`  (admins see everything)
 *  - the viewer is the booking owner (`job.user_id === viewerUserId`)
 *  - the viewer is the assigned Hauler (`job.assigned_driver === viewerUserId`)
 *
 * @param {object|null|undefined} job
 * @param {string|null|undefined} viewerUserId
 * @param {{ isAdmin?: boolean }} [opts]
 * @returns {boolean}
 */
export function canViewerSeeExactLocation(job, viewerUserId, opts) {
  if (opts && opts.isAdmin === true) return true;
  if (!job || !viewerUserId) return false;
  if (job.user_id && job.user_id === viewerUserId) return true;
  if (job.assigned_driver && job.assigned_driver === viewerUserId) return true;
  return false;
}

function fallbackZoneCoordinate(zone) {
  const tuple = (zone && ZONE_CENTROIDS[zone]) || DEFAULT_FALLBACK;
  const safeTuple = isInHaulerServiceArea(tuple[0], tuple[1]) ? tuple : DEFAULT_FALLBACK;
  return {
    lat: safeTuple[0],
    lng: safeTuple[1],
    precision: "zone",
    approximate: true,
  };
}

/**
 * Return a display coordinate for an available/unclaimed job.
 *
 * If the viewer is authorized (owner / assigned / admin), the booking's
 * accurate `lat`/`lng` are used. Otherwise the zone centroid is used.
 *
 * The viewer arguments are optional. Omitting them falls back to the
 * Phase 1 behavior (zone centroid for everyone) — that preserves the
 * default-privacy posture for any future caller that doesn't yet pass
 * viewer context.
 *
 * @param {object|null|undefined} job - request row.
 * @param {string|null|undefined} [viewerUserId]
 * @param {{ isAdmin?: boolean }} [opts]
 * @returns {{ lat: number, lng: number, precision: "exact"|"zone", approximate: boolean }}
 */
export function getAvailableJobDisplayCoordinate(job, viewerUserId, opts) {
  if (canViewerSeeExactLocation(job, viewerUserId, opts)) {
    const lat = Number(job?.lat);
    const lng = Number(job?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng) && isInHaulerServiceArea(lat, lng)) {
      return { lat, lng, precision: "exact", approximate: false };
    }
    // Authorized but coords missing/invalid/outside service area — fall through to zone.
  }
  const zone = job && typeof job.zone === "string" ? job.zone : null;
  return fallbackZoneCoordinate(zone);
}
